import { z } from "zod";
import type { UseFormRegister, FieldErrors } from "react-hook-form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ContactUsRepository } from "@repo/fetch-repository/contactUs";
import type { ContactUs } from "@repo/fetch-repository/contactUs";
import type {
  ComponentSettings,
  ComponentSettingsProps,
} from "@repo/editor/externalComponents";
import type { ComponentType } from "@repo/editor/types";
import React, { forwardRef, useState, useEffect } from "react";
import * as t from "@rekajs/types";
import { observer } from "mobx-react-lite";
import { useApp } from "@repo/app/useApp";
import { useEditor } from "@repo/editor/useEditor";
import { MediaRepository } from "@repo/fetch-repository/media";
import type { Media as MediaType } from "@repo/fetch-repository/media";
import type { PaginationType } from "@repo/fetch-repository/factory";
import { LoaderCircle, XIcon, CameraIcon } from "lucide-react";
import Image from "next/image";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../components/ui/pagination";
import {
  Dialog,
  DialogTitle,
  DialogHeader,
  DialogTrigger,
  DialogContent,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";

interface InternalChildSettingProps {
  activeSettings: ComponentSettings;
  template: t.Template;
  prop: string;
}

export const InternalImageSetting = observer(
  ({ prop, template }: InternalChildSettingProps) => {
    const editor = useEditor();
    const [imagesDialogShow, setImagesDialogShow] = useState(false);
    const [imagesLoading, setImagesLoading] = useState(false);
    const [images, setImages] = useState<MediaType[] | null>(null);
    const [pagination, setPagination] = useState<PaginationType | null>(null);
    const [currentPage, setCurrentPage] = useState(1);

    const handlePageChange = (page: number): void => {
      if (page !== currentPage) {
        setCurrentPage(page);
      }
    };
    useEffect(() => {
      const repository: MediaRepository = new MediaRepository();
      setImagesLoading(true);
      void repository
        .getMany({
          sort: "-id",
          page: currentPage,
        })
        .then((res) => {
          if (res.succeeded && res.data) {
            setImages(res.data);
            setPagination(res.pagination);
          }
          // TODO: handle errors
          // if (res.errors) {
          // }
        })
        .finally(() => {
          setImagesLoading(false);
        });
    }, [currentPage]);
    return (
      <div>
        <Dialog onOpenChange={setImagesDialogShow} open={imagesDialogShow}>
          <DialogTrigger asChild className="w-full">
            {template.props[prop] instanceof t.Literal &&
            template.props[prop].value ? (
              <div className="w-full group relative mt-4 rounded-lg">
                {/* eslint-disable-next-line prefer-named-capture-group -- disable */}
                {/\.(mp4|avi|mkv|mov|quicktime|x-quicktime)$/i.test(
                  String(template.props[prop].value)
                ) ? (
                  <video
                    autoPlay
                    className="w-full h-[200px] object-cover rounded-lg"
                    muted
                    src={String(template.props[prop].value)}
                  />
                ) : (
                  <Image
                    alt=""
                    className="w-full h-[200px] object-cover rounded-lg"
                    height={200}
                    onError={(e) => {
                      e.currentTarget.src = "/images/placeholder.png";
                    }}
                    src={String(template.props[prop].value)}
                    width={200}
                  />
                )}
                <button
                  className="hidden group-hover:flex outline-none flex-col absolute top-0 bottom-0 left-0 right-0 bg-[#00000050] items-center justify-center rounded-lg"
                  onClick={() => {
                    setImagesDialogShow(true);
                  }}
                  type="button"
                >
                  <CameraIcon className="text-white w-8 h-8" />
                  <span className="text-white">تغییر تصویر</span>
                </button>
                <Button
                  className="hidden group-hover:flex absolute w-8 h-8 -top-1 -right-1 text-white"
                  onClick={(e) => {
                    void editor.reka.change(() => {
                      (template.props[prop] as t.Literal) = t.literal({
                        value: String(""),
                      });
                    });
                    e.stopPropagation();
                  }}
                  size="icon"
                  variant="destructive"
                >
                  <XIcon />
                </Button>
              </div>
            ) : (
              <Button className="w-full" type="button" variant="secondary">
                انتخاب عکس
              </Button>
            )}
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px] sm:max-h-[400px] md:max-w-[600px] md:max-h-[500px] lg:max-w-[1250px] lg:max-h-[700px] overflow-auto">
            <DialogHeader>
              <DialogTitle>تصاویر</DialogTitle>
              {imagesLoading ? (
                <div className="flex items-center justify-center py-56">
                  <LoaderCircle className="animate-spin" size={30} />
                </div>
              ) : (
                <div className="pt-4 gap-7 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5">
                  {images && images.length > 0 ? (
                    images.map((image) => (
                      <button
                        className="text-start group outline-none"
                        key={image.id}
                        onClick={() => {
                          void editor.reka.change(() => {
                            (template.props[prop] as t.Literal) = t.literal({
                              value: image.url,
                            });
                          });
                          setImagesDialogShow(false);
                        }}
                        type="button"
                      >
                        <div className="relative">
                          <div className="rounded-lg hidden group-hover:flex absolute top-0 left-0 right-0 bottom-0 bg-[#00000070]  items-center justify-center text-white">
                            <span className="text-bold text-xl">انتخاب</span>
                          </div>
                          {image.type.startsWith("image") ? (
                            <Image
                              alt={image.alt || ""}
                              className="w-full h-[200px] object-cover rounded-lg"
                              height={200}
                              onError={(e) => {
                                e.currentTarget.src = "/images/placeholder.png";
                              }}
                              src={image.url}
                              width={200}
                            />
                          ) : (
                            <video
                              autoPlay
                              className="w-full h-[200px] object-cover rounded-lg"
                              muted
                              src={image.url}
                            />
                          )}
                        </div>
                        {image.title ? (
                          <>
                            <h3 className="text-lg text-bold text-nowrap overflow-hidden text-ellipsis whitespace-nowrap">
                              {image.title}
                            </h3>
                            {image.description ? (
                              <p className="text-sm text-nowrap overflow-hidden text-ellipsis whitespace-nowrap">
                                {image.description}
                              </p>
                            ) : null}
                          </>
                        ) : null}
                      </button>
                    ))
                  ) : (
                    <div className="flex items-center justify-center md:col-span-2 lg:col-span-3 py-40">
                      <p className="text-center">هیچ تصویری یافت نشد</p>
                    </div>
                  )}
                </div>
              )}
            </DialogHeader>
            {pagination ? (
              <Pagination>
                <PaginationContent>
                  {currentPage !== 1 ? (
                    <PaginationItem>
                      <PaginationPrevious
                        isActive={currentPage === 1}
                        onClick={() => {
                          handlePageChange(currentPage - 1);
                        }}
                        size="default"
                      />
                    </PaginationItem>
                  ) : null}
                  {Array.from(
                    { length: pagination.total_pages },
                    (_, index) => (
                      <PaginationItem key={index}>
                        <PaginationLink
                          className="rtl:font-dirooz"
                          isActive={currentPage === index + 1}
                          onClick={() => {
                            handlePageChange(index + 1);
                          }}
                          size="default"
                        >
                          {index + 1}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  )}
                  {currentPage !== pagination.total_pages ? (
                    <PaginationItem>
                      <PaginationNext
                        isActive={currentPage === pagination.total_pages}
                        onClick={() => {
                          handlePageChange(currentPage + 1);
                        }}
                        size="default"
                      />
                    </PaginationItem>
                  ) : null}
                </PaginationContent>
              </Pagination>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    );
  }
);

interface InputFieldProps {
  name: string;
  label: string;
  register: UseFormRegister<FormData>;
  errors?: FieldErrors<FormData>;
}

function InputField({
  name,
  label,
  register,
  errors,
}: InputFieldProps): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <label
        className="text-[10px] md:text-[14px] lg:text-[17px] text-[#423b54]"
        htmlFor={name}
      >
        {label}
      </label>
      <Input id={name} {...register(name as keyof typeof errors)} />
      {errors?.[name as keyof typeof errors] ? (
        <p className="text-red-500 text-sm mt-1">
          {errors[name as keyof typeof errors]?.message}
        </p>
      ) : null}
    </div>
  );
}

function defineFormSchema(props: ContactMainFormProps): z.ZodObject<{
  first_name: z.ZodString;
  last_name: z.ZodString;
  email: z.ZodString;
  mobile: z.ZodOptional<z.ZodString>;
  company_name: z.ZodString;
  type: z.ZodOptional<z.ZodString>;
  message: z.ZodString;
}> {
  return z.object({
    first_name: z.string().min(1, props.firstNameValidationMessage),
    last_name: z.string().min(1, props.lastNameValidationMessage),
    email: z.string().email(props.emailValidationMessage),
    mobile: z.string().min(11, props.phoneNumberValidationMessage).optional(),
    company_name: z.string().min(1, props.companyNameValidationMessage),
    type: z.string().optional(),
    message: z.string().min(1, props.messageValidationMessage),
  });
}

interface ContactMainFormProps {
  firstNameLabel: string;
  lastNameLabel: string;
  emailLabel: string;
  phoneNumberLabel: string;
  companyNameLabel: string;
  messageLabel: string;
  sendMessageLabel: string;
  selectCategoryLabel: string | null;
  // validation messages
  firstNameValidationMessage: string;
  lastNameValidationMessage: string;
  emailValidationMessage: string;
  phoneNumberValidationMessage: string;
  companyNameValidationMessage: string;
  messageValidationMessage: string;
}

type FormData = z.infer<ReturnType<typeof defineFormSchema>>;

export const ContactMainForm = observer(
  (props: ContactMainFormProps): React.JSX.Element => {
    const {
      register,
      handleSubmit,
      watch,
      setValue,
      formState: { errors },
    } = useForm<FormData>({
      resolver: zodResolver(defineFormSchema(props)),
    });

    const { toast } = useToast();

    const onSubmit = async (data: FormData): Promise<void> => {
      const repository: ContactUsRepository = new ContactUsRepository();
      const contactData: ContactUs = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        mobile: data.mobile,
        company_name: data.company_name,
        type: data.type,
        message: data.message,
      };
      const response = await repository.create(contactData);
      if (response.succeeded) {
        toast({
          title: "Message sent successfully",
          description: "Your message has been sent successfully",
        });
      } else {
        toast({
          title: "Message failed to send",
          description: "Your message failed to send",
        });
      }
      // Handle form submission
    };
    const app = useApp();
    const isEditor = app.rendererMode === "editor";

    return (
      <div className="w-full h-full md:pb-0">
        <form
          className="w-full lg:pt-16 md:pt-12 pt-9 px-10 lg:px-9 lg:pb-7 pb-6 shadow-xl rounded-xl grid grid-cols-1 md:grid-cols-2 gap-4"
          onSubmit={(e) => {
            if (isEditor) {
              e.preventDefault();
              return;
            }
            void handleSubmit(onSubmit)(e);
          }}
        >
          <InputField
            errors={errors}
            label={props.firstNameLabel}
            name="first_name"
            register={register}
          />

          <InputField
            errors={errors}
            label={props.lastNameLabel}
            name="last_name"
            register={register}
          />

          <InputField
            errors={errors}
            label={props.emailLabel}
            name="email"
            register={register}
          />

          <InputField
            errors={errors}
            label={props.phoneNumberLabel}
            name="mobile"
            register={register}
          />

          <InputField
            errors={errors}
            label={props.companyNameLabel}
            name="company_name"
            register={register}
          />

          <div className="flex flex-col gap-3">
            <label
              className="text-[10px] md:text-[14px] lg:text-[17px] text-[#423b54]"
              htmlFor="yourMessage"
            >
              {props.messageLabel}
            </label>
            <Select
              onValueChange={(value: string) => {
                setValue("type", value);
              }}
              value={watch("type") || ""}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {props.selectCategoryLabel &&
                  (JSON.parse(props.selectCategoryLabel) as string[]).map(
                    (category: string) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    )
                  )}
              </SelectContent>
            </Select>
            {errors.type ? (
              <p className="text-red-500 text-sm mt-1">{errors.type.message}</p>
            ) : null}
          </div>

          <div className="flex flex-col col-span-1 md:col-span-2 gap-2">
            <label
              className="text-[10px] md:text-[14px] lg:text-[17px] text-[#423b54]"
              htmlFor="yourMessage"
            >
              {props.messageLabel}
            </label>
            <Textarea id="message" {...register("message")} />
            {errors.message ? (
              <p className="text-red-500 text-sm mt-1">
                {errors.message.message}
              </p>
            ) : null}
          </div>

          <div className="col-span-1 md:col-span-2 flex justify-end">
            <Button>{props.sendMessageLabel}</Button>
          </div>
        </form>
      </div>
    );
  }
);

interface ContactHeaderProps {
  title: string;
  leftText: string;
  description: string;
  headerImage: string;
}

function ContactHeader({
  title,
  leftText,
  description,
  headerImage,
}: ContactHeaderProps): React.JSX.Element {
  return (
    <div className="relative order-last lg:order-first lg:p-16 md:p-12 p-7 flex items-center justify-center">
      <div className="top-0 left-0 right-0 bottom-0 absolute -z-10 header-bg">
        <img
          alt=""
          className="object-cover w-full h-full"
          onError={(e) => {
            e.currentTarget.src = "/images/placeholder.png";
          }}
          src={headerImage}
        />
      </div>
      <div className="relative z-20 text-white flex gap-x-7 md:gap-x-16">
        {leftText ? (
          <div className="flex items-center justify-start gap-4 write-vertical-left [writing-mode:vertical-lr] [textOrient:mixed]">
            <div className="h-[70px] w-px bg-white" />
            <span className="font-helvetica rtl:font-doran rtl:font-medium uppercase text-[15px] md:text-[20px] lg:text-[25px] font-[100] text-white">
              {leftText}
            </span>
            <div className="h-[70px] w-px bg-white" />
          </div>
        ) : null}
        <div className="flex flex-col justify-center gap-y-2 md:gap-y-3 lg:gap-y-4 ">
          <h1 className="font-helvetica rtl:font-doran rtl:font-bold text-[40.6px] md:text-[50px] font-[100] lg:text-[50px] xl:text-[66px] leading-[1.38]">
            {title}
          </h1>
          <p className="font-helvetica rtl:font-doran rtl:font-medium font-[100] text-[15px] md:text-[20px] lg:text-[25px] leading-[1.22]">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}

export const contactFormDefinition: ComponentSettingsProps = {
  headerImage: {
    type: "image",
    label: "تصویر صفحه",
  },
  title: {
    type: "string",
    label: "عنوان",
    default: "عنوان",
  },
  description: {
    type: "string",
    label: "توضیحات",
    default: "توضیحات",
  },
  leftText: {
    type: "string",
    label: "متن سمت راست",
    default: "متن سمت راست",
  },
  emailValidationMessage: {
    type: "string",
    label: "پیام اعتبار سنجی ایمیل",
    default: "پیام اعتبار سنجی ایمیل",
  },
  phoneNumberValidationMessage: {
    type: "string",
    label: "پیام اعتبار سنجی شماره تلفن",
    default: "پیام اعتبار سنجی شماره تلفن",
  },
  companyNameValidationMessage: {
    type: "string",
    label: "پیام اعتبار سنجی نام شرکت",
    default: "پیام اعتبار سنجی نام شرکت",
  },
  firstNameLabel: {
    type: "string",
    label: "نام",
    default: "نام",
  },
  lastNameLabel: {
    type: "string",
    label: "نام خانوادگی",
    default: "نام خانوادگی",
  },
  emailLabel: {
    type: "string",
    label: "ایمیل",
    default: "ایمیل",
  },
  phoneNumberLabel: {
    type: "string",
    label: "شماره تلفن",
    default: "شماره تلفن",
  },
  companyNameLabel: {
    type: "string",
    label: "نام شرکت",
    default: "نام شرکت",
  },
  messageLabel: {
    type: "string",
    label: "پیام",
    default: "پیام",
  },
  sendMessageLabel: {
    type: "string",
    label: "ارسال پیام",
    default: "ارسال پیام",
  },
  firstNameValidationMessage: {
    type: "string",
    label: "پیام اعتبار سنجی نام",
    default: "پیام اعتبار سنجی نام",
  },
  lastNameValidationMessage: {
    type: "string",
    label: "پیام اعتبار سنجی نام خانوادگی",
    default: "پیام اعتبار سنجی نام خانوادگی",
  },
  messageValidationMessage: {
    type: "string",
    label: "پیام اعتبار سنجی پیام",
    default: "پیام اعتبار سنجی پیام",
  },
  selectCategoryLabel: {
    type: "string",
    label: "انتخاب دسته بندی",
  },
};

interface ContactFormProps {
  title: string;
  description: string;
  headerImage: string;
  leftText: string;
  // form props
  emailValidationMessage: string;
  phoneNumberValidationMessage: string;
  companyNameValidationMessage: string;
  firstNameLabel: string;
  lastNameLabel: string;
  emailLabel: string;
  phoneNumberLabel: string;
  companyNameLabel: string;
  messageLabel: string;
  sendMessageLabel: string;
  firstNameValidationMessage: string;
  lastNameValidationMessage: string;
  messageValidationMessage: string;
  selectCategoryLabel: string;
}

export const ContactForm = forwardRef<HTMLDivElement, ContactFormProps>(
  (props, ref) => {
    return (
      <div
        className="grid grid-cols-1 lg:grid-cols-2 gap-3 relative z-10"
        ref={ref}
      >
        <ContactHeader
          description={props.description}
          headerImage={props.headerImage}
          leftText={props.leftText}
          title={props.title}
        />
        <ContactMainForm
          companyNameLabel={
            props.companyNameLabel ||
            String(contactFormDefinition.companyNameLabel.default)
          }
          companyNameValidationMessage={
            props.companyNameValidationMessage ||
            String(contactFormDefinition.companyNameValidationMessage.default)
          }
          emailLabel={
            props.emailLabel || String(contactFormDefinition.emailLabel.default)
          }
          emailValidationMessage={
            props.emailValidationMessage ||
            String(contactFormDefinition.emailValidationMessage.default)
          }
          firstNameLabel={
            props.firstNameLabel ||
            String(contactFormDefinition.firstNameLabel.default)
          }
          firstNameValidationMessage={
            props.firstNameValidationMessage ||
            String(contactFormDefinition.firstNameValidationMessage.default)
          }
          lastNameLabel={
            props.lastNameLabel ||
            String(contactFormDefinition.lastNameLabel.default)
          }
          lastNameValidationMessage={
            props.lastNameValidationMessage ||
            String(contactFormDefinition.lastNameValidationMessage.default)
          }
          messageLabel={
            props.messageLabel ||
            String(contactFormDefinition.messageLabel.default)
          }
          messageValidationMessage={
            props.messageValidationMessage ||
            String(contactFormDefinition.messageValidationMessage.default)
          }
          phoneNumberLabel={
            props.phoneNumberLabel ||
            String(contactFormDefinition.phoneNumberLabel.default)
          }
          phoneNumberValidationMessage={
            props.phoneNumberValidationMessage ||
            String(contactFormDefinition.phoneNumberValidationMessage.default)
          }
          selectCategoryLabel={props.selectCategoryLabel}
          sendMessageLabel={
            props.sendMessageLabel ||
            String(contactFormDefinition.sendMessageLabel.default)
          }
        />
      </div>
    );
  }
) as ComponentType<ContactFormProps>;

ContactForm.displayName = "ContactForm";
interface PropEditorSectionProps {
  activeSettings: ComponentSettings;
  template: t.Template;
}

const SettingTemplate = observer(
  ({ activeSettings, template }: PropEditorSectionProps): React.JSX.Element => {
    const editor = useEditor();
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [newItem, setNewItem] = useState("");
    const [editedValue, setEditedValue] = useState("");

    const getPropsBySection = (section: string): Record<string, string[]> => {
      return Object.keys(activeSettings.props).reduce<Record<string, string[]>>(
        (acc, prop) => {
          const currentSection = activeSettings.props[prop].section;
          if (currentSection === section) {
            if (!(currentSection in acc)) {
              acc[currentSection] = [];
            }
            acc[currentSection].push(prop);
          }
          return acc;
        },
        {}
      );
    };

    const getPropsWithoutSection = (): string[] => {
      return Object.keys(activeSettings.props).filter(
        (prop) => !activeSettings.props[prop].section
      );
    };

    const getAllSectionsNames = (): string[] => {
      const sections = Object.keys(activeSettings.props).map(
        (prop) => activeSettings.props[prop].section
      );
      return [...new Set(sections)] as string[];
    };

    const parseSelectCategoryLabel = (value: string): string[] => {
      try {
        return JSON.parse(value) as string[];
      } catch {
        return [];
      }
    };

    const updateCategories = (newCategories: string[]): void => {
      void editor.reka.change(() => {
        template.props.selectCategoryLabel = t.literal({
          value: JSON.stringify(newCategories),
        });
      });
    };

    const handleAddItem = (): void => {
      if (newItem.trim()) {
        const currentCategories = parseSelectCategoryLabel(
          template.props.selectCategoryLabel instanceof t.Literal
            ? String(template.props.selectCategoryLabel.value)
            : "[]"
        );
        updateCategories([...currentCategories, newItem.trim()]);
        setNewItem("");
      }
    };

    const handleDeleteItem = (index: number): void => {
      const currentCategories = parseSelectCategoryLabel(
        template.props.selectCategoryLabel instanceof t.Literal
          ? String(template.props.selectCategoryLabel.value)
          : "[]"
      );
      updateCategories(currentCategories.filter((_, i) => i !== index));
    };

    const renderSelectCategoryLabel = (): React.JSX.Element => {
      const categories = parseSelectCategoryLabel(
        template.props.selectCategoryLabel instanceof t.Literal
          ? String(template.props.selectCategoryLabel.value)
          : "[]"
      );

      return (
        <div className="flex flex-col gap-4">
          <div className="flex gap-2">
            <Input
              className="flex-1"
              onChange={(e) => {
                setNewItem(e.target.value);
              }}
              placeholder="افزودن دسته بندی جدید"
              value={newItem}
            />
            <Button onClick={handleAddItem}>افزودن</Button>
          </div>
          <div className="flex flex-col gap-3">
            {categories.map((category, index) => (
              <div
                className="border rounded-lg p-2 flex items-center justify-between"
                key={index}
              >
                {editingIndex === index ? (
                  <div className="flex gap-2 flex-1">
                    <Input
                      className="flex-1"
                      onChange={(e) => {
                        setEditedValue(e.target.value);
                      }}
                      ref={(input) => {
                        if (input && editingIndex === index) {
                          input.focus();
                        }
                      }}
                      value={editedValue}
                    />
                    <Button
                      onClick={() => {
                        if (editedValue.trim()) {
                          const currentCategories = parseSelectCategoryLabel(
                            template.props.selectCategoryLabel instanceof
                              t.Literal
                              ? String(template.props.selectCategoryLabel.value)
                              : "[]"
                          );
                          const updatedCategories = [...currentCategories];
                          updatedCategories[index] = editedValue.trim();
                          updateCategories(updatedCategories);
                          setEditingIndex(null);
                          setEditedValue("");
                        }
                      }}
                      size="sm"
                      variant="default"
                    >
                      ذخیره
                    </Button>
                  </div>
                ) : (
                  <span className="flex-1">{category}</span>
                )}
                <div className="flex gap-2">
                  {editingIndex !== index ? (
                    <Button
                      onClick={() => {
                        setEditedValue(category);
                        setEditingIndex(index);
                      }}
                      size="sm"
                      variant="ghost"
                    >
                      ویرایش
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        setEditingIndex(null);
                        setEditedValue("");
                      }}
                      size="sm"
                      variant="ghost"
                    >
                      انصراف
                    </Button>
                  )}
                  <Button
                    onClick={() => {
                      handleDeleteItem(index);
                    }}
                    size="sm"
                    variant="destructive"
                  >
                    حذف
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    };

    return (
      <div className="flex w-full">
        <div className="w-full">
          {getAllSectionsNames().map((section, index) => (
            <React.Fragment key={index}>
              <div className="py-2">
                <div className="text-sm pb-1 font-bold text-gray-500">
                  {section}
                </div>
                {section
                  ? getPropsBySection(section)[section].map((prop) => (
                      <div
                        className="mb-2 w-full border border-gray-200 rounded-md p-2"
                        key={prop}
                      >
                        <span className="inline-block text-sm mr-2 pb-1 flex-1">
                          {activeSettings.props[prop].label || prop}
                        </span>
                        {prop === "selectCategoryLabel" ? (
                          renderSelectCategoryLabel()
                        ) : (
                          <Input
                            className="flex-2"
                            onChange={(e) => {
                              void editor.reka.change(() => {
                                template.props[prop] = t.literal({
                                  value: String(e.target.value),
                                });
                              });
                            }}
                            type="text"
                            value={
                              template.props[prop] instanceof t.Literal
                                ? String(template.props[prop].value)
                                : ""
                            }
                          />
                        )}
                      </div>
                    ))
                  : null}
              </div>
            </React.Fragment>
          ))}
          {getPropsWithoutSection().length > 0 && (
            <div className="py-2">
              <div className="text-sm pb-1 font-bold text-gray-500">عمومی</div>
              {getPropsWithoutSection().map((prop) => (
                <div
                  className="mb-2 w-full border border-gray-200 rounded-md p-2"
                  key={prop}
                >
                  <span className="inline-block text-sm mr-2 pb-1 flex-1">
                    {activeSettings.props[prop].label || prop}
                  </span>
                  {/* eslint-disable-next-line no-nested-ternary -- disable */}
                  {prop === "selectCategoryLabel" ? (
                    renderSelectCategoryLabel()
                  ) : prop === "headerImage" ? (
                    <InternalImageSetting
                      activeSettings={activeSettings}
                      prop={prop}
                      template={template}
                    />
                  ) : (
                    <Input
                      className="flex-2"
                      onChange={(e) => {
                        void editor.reka.change(() => {
                          template.props[prop] = t.literal({
                            value: String(e.target.value),
                          });
                        });
                      }}
                      type="text"
                      value={
                        template.props[prop] instanceof t.Literal
                          ? String(template.props[prop].value)
                          : ""
                      }
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
);

export const contactFormSettings: ComponentSettings = {
  name: "EContactForm",
  props: contactFormDefinition,
  label: "فرم تماس با ما",
  settingTemplate: SettingTemplate,
  childrenAllowed: false,
  widgets: {
    label: "فرم تماس با ما",
    template: () =>
      t.componentTemplate({
        component: t.identifier({ name: "EContactForm", external: true }),
      }),
  },
};

ContactForm.componentSettings = contactFormSettings;
